/**
 * Modal de Encuesta Pendiente.
 *
 * Soporta 2 modos:
 *
 * 1) AUTOMÁTICO (sin props): se autofetchea /surveys/pending cuando hay un
 *    user logueado y se muestra solo si hay encuesta pendiente.
 *
 * 2) CONTROLADO (con prop `survey` + `onClose`): recibe la encuesta y un
 *    callback. El padre controla cuándo se muestra y qué hacer al terminar.
 *    Útil para mostrarla como GATE antes de continuar con otro flujo
 *    (ej: bloquear el envío del pedido hasta responder).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star, X, Send } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SurveyModal({ survey: surveyProp, onClose }) {
  const { user } = useAuth();
  const [internalSurvey, setInternalSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const isControlled = surveyProp !== undefined;
  // En modo controlado el survey lo manda el padre. En modo automático lo
  // resolvemos con el state interno.
  const survey = isControlled ? surveyProp : internalSurvey;
  const setSurvey = isControlled ? () => {} : setInternalSurvey;

  useEffect(() => {
    if (isControlled) return; // En modo controlado no auto-fetch
    if (!user) return;
    let cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      axios.get(`${API}/surveys/pending`)
        .then(r => {
          if (cancelled) return;
          const s = r.data?.survey;
          if (s) {
            setInternalSurvey(s);
            setAnswers({});
            setError('');
          }
        })
        .catch(() => {});
    }, 900);
    return () => { cancelled = true; clearTimeout(t); };
  }, [user, isControlled]);

  if (!survey || !user) return null;

  const setA = (qid, v) => setAnswers(p => ({ ...p, [qid]: v }));

  const submit = async (skip = false) => {
    setError('');
    if (!skip) {
      // Validación local de obligatorias
      for (const q of (survey.questions || [])) {
        if (q.required) {
          const v = answers[q.id];
          if (v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) {
            setError(`Falta responder: "${q.text}"`);
            return;
          }
        }
      }
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/surveys/${survey.id}/respond`, { answers, skipped: skip });
      setSurvey(null);
      setAnswers({});
      if (onClose) onClose({ submitted: !skip, skipped: skip });
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  const canSkip = !!survey.allow_skip;

  return (
    <div
      data-testid="survey-modal-backdrop"
      className="fixed inset-0 z-[1000] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => {
        // Permitir cerrar tocando afuera SOLO si allow_skip
        if (canSkip && e.target === e.currentTarget) submit(true);
      }}
    >
      <div
        data-testid="survey-modal"
        className="bg-white w-full sm:max-w-xl sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-zinc-100 flex items-start gap-3">
          <div className="flex-1">
            <h2 className="text-lg font-black text-zinc-900" data-testid="survey-title">{survey.title}</h2>
            {survey.description && (
              <p className="text-sm text-zinc-500 mt-1">{survey.description}</p>
            )}
            {!canSkip && (
              <p className="text-[11px] text-amber-700 mt-2 font-bold uppercase tracking-wide">
                ★ Tu opinión es importante. Por favor respondé para continuar.
              </p>
            )}
          </div>
          {canSkip && (
            <button onClick={() => submit(true)} className="text-zinc-400 hover:text-zinc-700 p-1" data-testid="survey-skip-btn" title="Omitir">
              <X size={18} />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {(survey.questions || []).map((q, idx) => (
            <div key={q.id} data-testid={`survey-q-${idx}`}>
              <label className="block text-sm font-bold text-zinc-800 mb-2">
                {idx + 1}. {q.text}
                {q.required && <span className="text-rose-500 ml-1">*</span>}
              </label>
              {q.type === 'text' && (
                <textarea
                  value={answers[q.id] || ''}
                  onChange={(e) => setA(q.id, e.target.value)}
                  className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none"
                  rows={3}
                  data-testid={`survey-answer-${idx}`}
                />
              )}
              {q.type === 'single' && (
                <div className="space-y-1.5">
                  {(q.options || []).map(opt => (
                    <label key={opt} className="flex items-center gap-2 cursor-pointer text-sm px-3 py-2 rounded-lg border border-zinc-200 hover:bg-amber-50">
                      <input type="radio" name={`q-${q.id}`}
                        checked={answers[q.id] === opt}
                        onChange={() => setA(q.id, opt)}
                        data-testid={`survey-radio-${idx}-${opt}`} />
                      <span>{opt}</span>
                    </label>
                  ))}
                </div>
              )}
              {q.type === 'multi' && (
                <div className="space-y-1.5">
                  {(q.options || []).map(opt => {
                    const arr = Array.isArray(answers[q.id]) ? answers[q.id] : [];
                    const checked = arr.includes(opt);
                    return (
                      <label key={opt} className="flex items-center gap-2 cursor-pointer text-sm px-3 py-2 rounded-lg border border-zinc-200 hover:bg-amber-50">
                        <input type="checkbox" checked={checked}
                          onChange={() => {
                            const next = checked ? arr.filter(x => x !== opt) : [...arr, opt];
                            setA(q.id, next);
                          }}
                          data-testid={`survey-check-${idx}-${opt}`} />
                        <span>{opt}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              {q.type === 'rating' && (
                <div className="flex items-center gap-1.5">
                  {[1, 2, 3, 4, 5].map(n => {
                    const active = Number(answers[q.id]) >= n;
                    return (
                      <button key={n} type="button" onClick={() => setA(q.id, n)}
                        className={`p-1 rounded-full transition-transform hover:scale-110 ${active ? 'text-amber-400' : 'text-zinc-300'}`}
                        data-testid={`survey-star-${idx}-${n}`}>
                        <Star size={28} fill={active ? 'currentColor' : 'none'} />
                      </button>
                    );
                  })}
                  {answers[q.id] && <span className="ml-2 text-xs text-zinc-600">{answers[q.id]}/5</span>}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-zinc-100 bg-zinc-50/50">
          {error && <div className="text-xs text-rose-600 font-bold mb-2" data-testid="survey-error">{error}</div>}
          <div className="flex gap-2">
            {canSkip && (
              <button onClick={() => submit(true)} disabled={submitting}
                className="flex-1 sm:flex-initial px-4 py-2.5 border border-zinc-300 text-zinc-700 rounded-lg text-sm font-bold hover:bg-white">
                Ahora no
              </button>
            )}
            <button onClick={() => submit(false)} disabled={submitting}
              data-testid="survey-submit-btn"
              className="flex-1 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 disabled:bg-zinc-300 text-white rounded-lg text-sm font-bold flex items-center justify-center gap-2 shadow">
              <Send size={14} /> {submitting ? 'Enviando…' : 'Enviar respuesta'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
